import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Typography,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ListAltIcon from "@material-ui/icons/ListAlt";
import ShuffleIcon from "@material-ui/icons/Shuffle";
import VisibilityIcon from "@material-ui/icons/Visibility";
import WarningIcon from "@material-ui/icons/Warning";
import { makeStyles } from "@material-ui/core/styles";
import managerFilesApi from "api/managerFiles";
import clsx from "clsx";
import InputField from "components/FormFields/InputField";
import ExamQuestionCard from "components/ExamQuestionCard";
import ExamStudentPreviewModal from "components/ExamStudentPreviewModal";
import useDebouncedSetter from "hooks/useDebounceSetter";
import { AnswerContent, ExamQuestions, ListExam, Question } from "models/managerTest";
import moment from "moment";
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import DetailExam from "./DetailExam";

export interface CreateQuestionProps {
  valueForm: Question | undefined;
  setCreateQuestion: (e: any) => void;
  setValueForm: (e: any) => void;
  listQuestionsAdded: ExamQuestions[];
  setIsPickOldExam: (e: any) => void;
  initialRandomCount?: number;
}

export default function CreateQuestion({
  valueForm,
  setCreateQuestion,
  setValueForm,
  listQuestionsAdded,
  setIsPickOldExam,
  initialRandomCount,
}: CreateQuestionProps) {
  const { t } = useTranslation();
  const initialValues = useMemo(
    () => ({
      title: valueForm?.title || "",
    }),
    [valueForm]
  );

  const createSchema = (questions: string[], answers: any) =>
    yup.object().shape(
      questions.reduce(
        (acc: any, key: string) => ({
          ...acc,
          [key]: yup.string().min(1, `${t("managerTest.vuilongnhapcauhoibaithi")}`),
          title: yup.string().min(1, `${t("managerTest.vuilongnhaptieudebaithi")}`),
          ...answers,
        }),
        {}
      )
    );

  const useStyles = makeStyles((theme) => ({
    wrapper: {
      width: "100%",
      minHeight: "calc(100vh - 200px)",
      display: "flex",
      flexDirection: "column",
    },
    topStatsBar: {
      position: "sticky",
      top: 16,
      zIndex: 90,
      backgroundColor: "rgba(255, 255, 255, 0.92)",
      backdropFilter: "blur(12px)",
      borderRadius: 16,
      padding: "14px 24px",
      marginBottom: 20,
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 20px rgba(15, 23, 42, 0.05)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 16,
      [theme.breakpoints.down("sm")]: {
        padding: "12px 16px",
        marginBottom: 12,
      },
    },
    mobileFloatingTopNav: {
      display: "none",
      [theme.breakpoints.down("sm")]: {
        display: "block",
        position: "sticky",
        top: 70,
        zIndex: 95,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(12px)",
        borderRadius: 16,
        border: "1px solid #CBD5E1",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.1)",
        padding: "10px 14px",
        marginTop: 16,
        marginBottom: 16,
        transition: "border-radius 0.2s ease, box-shadow 0.2s ease",
      },
    },
    mobileFloatingTopNavSticky: {
      [theme.breakpoints.down("sm")]: {
        borderRadius: "0 0 16px 16px !important",
        borderTop: "none !important",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.12) !important",
      },
    },
    mobileBadgeScrollContainer: {
      display: "flex",
      alignItems: "center",
      overflowX: "auto",
      gap: 8,
      padding: "10px 6px",
      minHeight: 56,
      WebkitOverflowScrolling: "touch",
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none",
      },
    },
    titleCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 16,
      padding: "20px 24px",
      marginBottom: 20,
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 16px rgba(15, 23, 42, 0.03)",
    },
    sidebarCard: {
      backgroundColor: "#FFFFFF",
      borderRadius: 16,
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)",
      padding: "20px",
      position: "sticky",
      top: 90,
      maxHeight: "calc(100vh - 120px)",
      overflowY: "auto",
      [theme.breakpoints.down("sm")]: {
        display: "none",
      },
    },
    sidebarTitle: {
      fontWeight: 700,
      color: "#0F172A",
      fontSize: "0.95rem",
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 14,
    },
    questionGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(40px, 1fr))",
      gap: 8,
      marginTop: 12,
      marginBottom: 16,
      padding: "6px 4px",
    },
    questionBadge: {
      minWidth: 40,
      height: 40,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 700,
      fontSize: 13,
      cursor: "pointer",
      transition: "all 0.2s ease-in-out",
      userSelect: "none",
      flexShrink: 0,
      boxSizing: "border-box",
      "&:hover": {
        transform: "translateY(-2px)",
      },
    },
    questionBadgeValid: {
      backgroundColor: "#ECFDF5",
      color: "#065F46",
      border: "1px solid #A7F3D0",
    },
    questionBadgeInvalid: {
      backgroundColor: "#FEF3C7",
      color: "#92400E",
      border: "1px solid #FDE68A",
    },
    questionBadgeActive: {
      boxShadow: "0 0 0 2px #2563EB, 0 4px 12px rgba(37, 99, 235, 0.35)",
      backgroundColor: "#2563EB !important",
      color: "#FFFFFF !important",
      border: "1px solid #1D4ED8 !important",
      transform: "translateY(-2px)",
      fontWeight: 800,
      zIndex: 2,
    },
    inputShuffle: {
      border: "1px solid #CBD5E1",
      borderRadius: 8,
      padding: "4px 8px",
      width: 60,
      textAlign: "center",
      fontWeight: 700,
      fontSize: "0.9rem",
      outline: "none",
      "&:focus": {
        borderColor: "#2563EB",
        boxShadow: "0 0 0 2px rgba(37, 99, 235, 0.2)",
      },
    },
    stickyFooter: {
      position: "sticky",
      bottom: 16,
      zIndex: 100,
      backgroundColor: "rgba(255, 255, 255, 0.94)",
      backdropFilter: "blur(12px)",
      borderRadius: 16,
      padding: "14px 24px",
      border: "1px solid #E2E8F0",
      boxShadow: "0 10px 30px rgba(15, 23, 42, 0.1)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: "auto",
      flexWrap: "wrap",
      gap: 12,
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        alignItems: "stretch",
        padding: "12px 14px",
        bottom: 8,
        borderRadius: 14,
        gap: 10,
      },
    },
    footerBtnGroup: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        justifyContent: "space-between",
        gap: 8,
        "& > button": {
          flex: 1,
          marginRight: "0 !important",
          justifyContent: "center",
        },
      },
    },
    btnPrimary: {
      background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
      color: "#FFFFFF",
      borderRadius: 12,
      padding: "10px 24px",
      fontWeight: 700,
      fontSize: "0.9rem",
      textTransform: "none",
      boxShadow: "0 4px 14px rgba(37, 99, 235, 0.3)",
      "&:hover": {
        background: "linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)",
      },
      [theme.breakpoints.down("sm")]: {
        flex: 1,
        padding: "12px 14px",
        fontSize: "0.85rem",
      },
    },
    btnSecondary: {
      backgroundColor: "#F1F5F9",
      color: "#475569",
      borderRadius: 12,
      padding: "10px 20px",
      fontWeight: 600,
      fontSize: "0.9rem",
      textTransform: "none",
      border: "1px solid #CBD5E1",
      marginRight: 12,
      "&:hover": {
        backgroundColor: "#E2E8F0",
        color: "#0F172A",
      },
      [theme.breakpoints.down("sm")]: {
        flex: 1,
        marginRight: 0,
        padding: "12px 14px",
        fontSize: "0.85rem",
      },
    },
  }));

  const classes = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDetailExam, setShowDetailExam] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [valueInputShuffle, setValueInputShuffle] = useState<number | undefined>(initialRandomCount);
  const [questions, setQuestions] = useState<any>([]);
  const setDebouncedQuestions = useDebouncedSetter(setQuestions, 300);
  const setDebounceTitle = useDebouncedSetter(setValueForm, 300);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(0);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const targetParents: HTMLElement[] = [];
    const originalOverflows: string[] = [];

    if (containerRef.current) {
      let parent = containerRef.current.parentElement;
      while (parent && parent.tagName !== "MAIN" && parent.tagName !== "BODY") {
        const computedStyle = window.getComputedStyle(parent);
        if (
          computedStyle.overflow === "hidden" ||
          computedStyle.overflowX === "hidden" ||
          computedStyle.overflowY === "hidden"
        ) {
          targetParents.push(parent);
          originalOverflows.push(parent.style.overflow);
          parent.style.overflow = "visible";
        }
        parent = parent.parentElement;
      }
    }

    return () => {
      targetParents.forEach((parent, index) => {
        if (parent) {
          parent.style.overflow = originalOverflows[index];
        }
      });
    };
  }, []);

  useEffect(() => {
    setValueInputShuffle(initialRandomCount);
  }, [initialRandomCount]);

  useEffect(() => {
    const listAdded: any =
      listQuestionsAdded?.map((x, idx) => {
        const pointPerItem = parseFloat(
          (valueForm?.totalPoint / listQuestionsAdded.length).toFixed(1)
        );
        const remainingPoint = parseFloat(
          (valueForm?.totalPoint - pointPerItem * (listQuestionsAdded.length - 1)).toFixed(1)
        );
        return {
          id: Math.random(),
          questionContent: x.question?.content,
          point: idx < listQuestionsAdded.length - 1 ? pointPerItem : remainingPoint,
          imageFileIDList:
            (x.question?.imageFileList?.map((y: any) => ({
              id: y.imageFileID,
              url: y.imageFile?.url,
            }))) || [],
          answers:
            x.question?.answers?.map((z: any) => ({
              answerContent: z.content || "",
              isRightAnswer: z.isRightAnswer || false,
            })) || [],
        };
      }) || [];

    const newQuestion: any = {
      id: Math.random(),
      questionContent: "",
      point: "",
      imageFileIDList: [],
      answers: [
        { answerContent: "", isRightAnswer: false },
        { answerContent: "", isRightAnswer: false },
        { answerContent: "", isRightAnswer: false },
        { answerContent: "", isRightAnswer: false },
      ],
    };

    if (listQuestionsAdded.length > 0) {
      setQuestions([...listAdded]);
    } else {
      setQuestions([newQuestion]);
    }
  }, [listQuestionsAdded, valueForm?.totalPoint]);

  useEffect(() => {
    if (!questions || questions.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            const index = parseInt(id.replace("question-", ""), 10) - 1;
            if (!isNaN(index)) {
              setActiveQuestionIndex(index);
            }
          }
        });
      },
      {
        root: null,
        rootMargin: "-20% 0px -50% 0px",
        threshold: 0.1,
      }
    );

    questions.forEach((_: any, idx: number) => {
      const el = document.getElementById(`question-${idx + 1}`);
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, [questions]);

  useEffect(() => {
    const container = document.getElementById("mobile-badge-container");
    const badge = document.getElementById(`mobile-badge-${activeQuestionIndex}`);
    if (container && badge) {
      const containerRect = container.getBoundingClientRect();
      const badgeRect = badge.getBoundingClientRect();
      const scrollLeftTarget =
        container.scrollLeft +
        (badgeRect.left - containerRect.left) -
        containerRect.width / 2 +
        badgeRect.width / 2;
      container.scrollTo({ left: Math.max(0, scrollLeftTarget), behavior: "smooth" });
    }
  }, [activeQuestionIndex]);

  const [isStickyMobile, setIsStickyMobile] = useState(false);

  useEffect(() => {
    const sentinel = document.getElementById("sticky-sentinel");
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStickyMobile(!entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: "-72px 0px 0px 0px",
        threshold: 0,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, []);

  const { control, handleSubmit } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(
      createSchema(
        questions.map((x: any, index: number) => `questions-${index}`),
        questions
          .reduce((prevValue: any, currentValue: any, currentIndex: number) => {
            return [
              ...prevValue,
              ...currentValue?.answers?.map(
                (answer: any, index: number) => `answerContent-${currentIndex}-${index}`
              ),
            ];
          }, [])
          .reduce((acc: string[], key: string) => {
            return {
              ...acc,
              [key]: yup.string().min(1, `${t("managerTest.vuilongnhapdapancuacauhoi")}`),
            };
          }, {})
      )
    ),
  });

  const getNormalizedQuestionContent = (content?: string) =>
    (content || "").trim().replace(/\s+/g, " ").toLowerCase();

  const totalAssignedPoints = useMemo(() => {
    return questions.reduce((sum: number, item: any) => sum + (Number(item.point) || 0), 0);
  }, [questions]);

  const totalRequiredPoints = Number(valueForm?.totalPoint) || 0;

  const isScoreMatching = useMemo(() => {
    if (totalRequiredPoints <= 0) return true;
    return Math.abs(totalAssignedPoints - totalRequiredPoints) < 0.01;
  }, [totalAssignedPoints, totalRequiredPoints]);

  const autoDistributePoints = () => {
    if (questions.length === 0 || totalRequiredPoints <= 0) return;
    const avgPoint = Number((totalRequiredPoints / questions.length).toFixed(2));
    const updated = questions.map((q: any) => ({
      ...q,
      point: avgPoint,
    }));
    setQuestions(updated);
    toast.success(`Đã chia đều ${avgPoint}đ cho ${questions.length} câu hỏi!`);
  };

  const scrollToQuestion = (idx: number) => {
    const element = document.getElementById(`question-${idx + 1}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleAddQuestion = () => {
    const nextIndex = questions.length;
    setQuestions((qs: any) => [
      ...qs,
      {
        id: Math.random(),
        questionContent: "",
        point: "",
        imageFileIDList: [],
        answers: [
          { answerContent: "", isRightAnswer: false },
          { answerContent: "", isRightAnswer: false },
          { answerContent: "", isRightAnswer: false },
          { answerContent: "", isRightAnswer: false },
        ],
      },
    ]);
    setActiveQuestionIndex(nextIndex);
    setTimeout(() => {
      scrollToQuestion(nextIndex);
    }, 100);
  };

  const handleCopyQuestion = (index: number) => {
    const qs = [...questions];
    let newQs = qs[index];
    const isDuplicate = questions.some(
      (item: any, idx: number) =>
        idx !== index && item.questionContent?.trim() === newQs.questionContent?.trim()
    );
    if (isDuplicate) {
      toast.error("Câu hỏi này đã tồn tại trong đề");
      return;
    }
    const nextIndex = questions.length;
    setQuestions([
      ...questions,
      {
        id: Math.random(),
        questionContent: newQs.questionContent,
        point: newQs.point,
        imageFileIDList: newQs.imageFileIDList ? [...newQs.imageFileIDList] : [],
        answers: newQs.answers.map((answer: AnswerContent) => ({
          answerContent: answer.answerContent,
          isRightAnswer: answer.isRightAnswer,
        })),
      },
    ]);
    setActiveQuestionIndex(nextIndex);
    toast.success(`Đã sao chép Câu ${index + 1}`);
    setTimeout(() => {
      scrollToQuestion(nextIndex);
    }, 100);
  };

  const handleDeleteQuestion = (index: number) => {
    const qs = [...questions];
    if (questions.length > 1) {
      qs.splice(index, 1);
      setQuestions(qs);
    } else {
      toast.warning("Đề thi phải có ít nhất 1 câu hỏi");
    }
  };

  const handleDeleteAnswer = (i: number, j: number) => {
    const optionsOfQuestion = [...questions];
    if (optionsOfQuestion[i].answers.length > 1) {
      optionsOfQuestion[i].answers.splice(j, 1);
      setQuestions(optionsOfQuestion);
    }
  };

  const handleChangeOptionValue = (value: string, i: number, j: number) => {
    const optionsOfQuestion = [...questions];
    optionsOfQuestion[i].answers[j].answerContent = value;
    setDebouncedQuestions(optionsOfQuestion);
  };

  const handleChangeQuestionValue = (value: string, i: number) => {
    const optionsOfQuestion = [...questions];
    optionsOfQuestion[i].questionContent = value;
    setDebouncedQuestions(optionsOfQuestion);
  };

  const handleChangePointValue = (value: string, i: number) => {
    const optionsOfQuestion = [...questions];
    optionsOfQuestion[i].point = value;
    setDebouncedQuestions(optionsOfQuestion);
  };

  const handleChangeTitleQuestion = (value: string) => {
    setDebounceTitle({ ...valueForm, title: value });
  };

  const handleChangeRadioAnswer = (i: number, j: number) => {
    const optionsOfQuestion = [...questions];
    optionsOfQuestion[i].answers = optionsOfQuestion[i].answers.map(
      (answer: AnswerContent, index: number) => ({
        ...answer,
        isRightAnswer: index === j,
      })
    );
    setQuestions(optionsOfQuestion);
  };

  const handleAddPicture = async (acceptedFiles: any, i: number) => {
    const formData = new FormData();
    try {
      acceptedFiles.forEach((acceptedFile: any) => {
        formData.append("attachments", acceptedFile);
      });
      const data = await managerFilesApi.uploadFiles(formData);
      const optionsOfQuestion = [...questions];
      optionsOfQuestion[i].imageFileIDList = [
        ...(optionsOfQuestion[i].imageFileIDList || []),
        ...data.body?.attachmentList,
      ];
      setQuestions(optionsOfQuestion);
      toast.success("Đã đính kèm hình ảnh");
    } catch (error) {
      toast.error("Tải ảnh thất bại");
    }
  };

  const handleDeleteImage = (indexArr: number, idxImage: number) => {
    const optionsOfQuestion = [...questions];
    optionsOfQuestion[indexArr].imageFileIDList.splice(idxImage, 1);
    setQuestions(optionsOfQuestion);
  };

  const handleAddAnswer = (index: number) => {
    const optionsOfQuestion = [...questions];
    if (optionsOfQuestion[index].answers.length < 10) {
      optionsOfQuestion[index].answers.push({
        answerContent: "",
        isRightAnswer: false,
      });
      setQuestions(optionsOfQuestion);
    } else {
      toast.error(`${t("managerTest.toida10cautraloi")}`);
    }
  };

  const handleSubmitForm = async () => {
    const normalizedContents = questions
      .map((q: any) => getNormalizedQuestionContent(q.questionContent))
      .filter((content: string) => content.length > 0);
    const duplicateCount = normalizedContents.length - new Set(normalizedContents).size;
    if (duplicateCount > 0) {
      toast.error(`Có ${duplicateCount} câu hỏi bị trùng nội dung. Vui lòng kiểm tra lại.`);
      return;
    }

    if (valueForm && valueForm?.totalPoint > 0) {
      const pointQuestion = questions.reduce((x: any, item: any) => (x += Number(item.point)), 0);
      if (pointQuestion > valueForm?.totalPoint) {
        toast.error(`${t("managerTest.tongsodiemlonhondiemtongdanhap")}`);
        return;
      } else if (
        pointQuestion === valueForm?.totalPoint &&
        questions.some((item: any) => item.point === "")
      ) {
        toast.error(`${t("managerTest.tongsodiemlonhondiemtongdanhap")}`);
        return;
      } else if (
        pointQuestion < valueForm?.totalPoint &&
        questions.every((item: any) => item.point !== "")
      ) {
        toast.error(`${t("managerTest.tongsodiemlonhondiemtongdanhap")}`);
        return;
      }
    }

    if (valueForm?.category === "essay") {
      setShowDetailExam(true);
      setSearchParams({ step: "3" });
      return;
    }

    let hasError = false;
    if (valueForm?.category === "multipleChoice") {
      questions.forEach((x: any, idx: number) => {
        if (x.answers.every((y: AnswerContent) => !y.isRightAnswer)) {
          toast.error(`${t("managerTest.vuilongnhapdapancuacauhoi")} ${idx + 1}`);
          hasError = true;
        }
      });
    }

    if (hasError) {
      setShowDetailExam(false);
      return;
    }

    setShowDetailExam(true);
    setSearchParams({ step: "3" });
  };

  return (
    <div ref={containerRef} className={classes.wrapper}>
      {showDetailExam ? (
        <DetailExam
          listQuestion={questions}
          valueForm={valueForm}
          setShowDetailExam={(val) => {
            setShowDetailExam(val);
            if (!val) setSearchParams({ step: "2", mode: "create" });
          }}
          valueInputShuffle={valueInputShuffle}
        />
      ) : (
        <form onSubmit={handleSubmit(handleSubmitForm)} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Top Sticky Stats Bar */}
          <Box className={classes.topStatsBar}>
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              <Chip
                label={`Tổng số câu: ${questions.length}`}
                style={{ backgroundColor: "#0F172A", color: "#FFFFFF", fontWeight: 700 }}
              />
              <Chip
                icon={isScoreMatching ? <CheckCircleIcon style={{ color: "#10B981" }} /> : <WarningIcon style={{ color: "#F59E0B" }} />}
                label={`Điểm đã gán: ${totalAssignedPoints} / ${totalRequiredPoints}đ`}
                style={{
                  backgroundColor: isScoreMatching ? "#ECFDF5" : "#FEF3C7",
                  color: isScoreMatching ? "#065F46" : "#92400E",
                  fontWeight: 700,
                  border: `1px solid ${isScoreMatching ? "#A7F3D0" : "#FDE68A"}`,
                }}
              />
            </Box>

            <Box display="flex" alignItems="center" style={{ gap: 10 }}>
              {totalRequiredPoints > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={autoDistributePoints}
                  style={{
                    borderRadius: 8,
                    fontWeight: 600,
                    fontSize: "0.78rem",
                    borderColor: "#CBD5E1",
                    color: "#334155",
                    textTransform: "none",
                  }}
                >
                  ⚡ Chia đều điểm
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={handleAddQuestion}
                style={{
                  backgroundColor: "#2563EB",
                  color: "#FFFFFF",
                  borderRadius: 8,
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  textTransform: "none",
                }}
              >
                Thêm câu hỏi mới
              </Button>
            </Box>
          </Box>

          <div id="sticky-sentinel" style={{ height: 1, marginTop: -1 }} />

          {/* Mobile Badge Scroll Container */}
          <Box
            id="mobile-floating-top-nav"
            className={clsx(
              classes.mobileFloatingTopNav,
              isStickyMobile && classes.mobileFloatingTopNavSticky
            )}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>
                Danh sách câu hỏi ({questions.length})
              </Typography>
              <Typography style={{ fontSize: 12, fontWeight: 600, color: "#64748B" }}>
                Chạm để chuyển câu
              </Typography>
            </Box>
            <div id="mobile-badge-container" className={classes.mobileBadgeScrollContainer}>
              {questions.map((q: any, idx: number) => {
                const hasValidContent = Boolean(q.questionContent && q.questionContent.trim());
                const hasValidRightAns = valueForm?.category === "essay" || q.answers?.some((a: any) => a.isRightAnswer);
                const isValid = hasValidContent && hasValidRightAns;
                const isActive = activeQuestionIndex === idx;

                return (
                  <div
                    key={idx}
                    id={`mobile-badge-${idx}`}
                    className={clsx(
                      classes.questionBadge,
                      isValid ? classes.questionBadgeValid : classes.questionBadgeInvalid,
                      isActive && classes.questionBadgeActive
                    )}
                    onClick={() => {
                      setActiveQuestionIndex(idx);
                      scrollToQuestion(idx);
                    }}
                  >
                    {idx + 1}
                  </div>
                );
              })}
            </div>
          </Box>

          <Paper className={classes.titleCard} elevation={0}>
            <InputField
              name="title"
              label="TIÊU ĐỀ ĐỀ THI"
              control={control}
              rows={2}
              multiline
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                handleChangeTitleQuestion(event.target.value)
              }
            />
          </Paper>

          {/* 100% REUSED GRID LAYOUT FROM UPDATE EXAM: Sidebar 3 cols on left, Question Cards 9 cols on right */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={4} lg={3}>
              <Paper className={classes.sidebarCard} elevation={0}>
                <div className={classes.sidebarTitle}>
                  <ListAltIcon style={{ color: "#2563EB" }} />
                  <span>Danh sách câu hỏi</span>
                </div>

                <div className={classes.questionGrid}>
                  {questions.map((q: any, idx: number) => {
                    const hasValidContent = Boolean(q.questionContent && q.questionContent.trim());
                    const hasValidRightAns = valueForm?.category === "essay" || q.answers?.some((a: any) => a.isRightAnswer);
                    const isValid = hasValidContent && hasValidRightAns;
                    const isActive = activeQuestionIndex === idx;

                    return (
                      <div
                        key={idx}
                        className={clsx(
                          classes.questionBadge,
                          isValid ? classes.questionBadgeValid : classes.questionBadgeInvalid,
                          isActive && classes.questionBadgeActive
                        )}
                        onClick={() => {
                          setActiveQuestionIndex(idx);
                          scrollToQuestion(idx);
                        }}
                      >
                        {idx + 1}
                      </div>
                    );
                  })}
                </div>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleAddQuestion}
                  style={{
                    borderRadius: 10,
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    borderColor: "#2563EB",
                    color: "#2563EB",
                    textTransform: "none",
                    marginTop: 8,
                  }}
                >
                  Thêm câu hỏi ({questions.length + 1})
                </Button>
              </Paper>
            </Grid>

            <Grid item xs={12} md={8} lg={9}>
              {questions.map((question: any, index: number) => (
                <div id={`question-${index + 1}`} key={question.id || index}>
                  <ExamQuestionCard
                    question={question}
                    index={index}
                    mode="edit"
                    control={control}
                    isEssay={valueForm?.category === "essay"}
                    onChangeQuestionContent={(val) => handleChangeQuestionValue(val, index)}
                    onChangePoint={(val) => handleChangePointValue(val, index)}
                    onChangeOptionValue={(val, optionIdx) => handleChangeOptionValue(val, index, optionIdx)}
                    onSelectRightAnswer={(optionIdx) => handleChangeRadioAnswer(index, optionIdx)}
                    onAddAnswer={() => handleAddAnswer(index)}
                    onDeleteAnswer={(optionIdx) => handleDeleteAnswer(index, index)}
                    onCopyQuestion={() => handleCopyQuestion(index)}
                    onDeleteQuestion={() => handleDeleteQuestion(index)}
                    onAddPicture={(files) => handleAddPicture(files, index)}
                    onDeletePicture={(idxImg) => handleDeleteImage(index, idxImg)}
                  />
                </div>
              ))}
            </Grid>
          </Grid>

          {/* Sticky Bottom Action Bar Reused 100% from Update Exam */}
          <Box className={classes.stickyFooter}>
            <Box display="flex" alignItems="center" style={{ gap: 16, flexWrap: "wrap" }}>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <ShuffleIcon style={{ color: "#4F46E5", fontSize: 20 }} />
                <Typography variant="body2" style={{ fontWeight: 600, color: "#334155" }}>
                  Trộn ngẫu nhiên:
                </Typography>
                <input
                  className={classes.inputShuffle}
                  type="number"
                  value={valueInputShuffle ?? ""}
                  onChange={(e) => {
                    const value = e.target.value === "" ? undefined : Number(e.target.value);
                    setValueInputShuffle(value);
                  }}
                />
                <Typography variant="body2" style={{ fontWeight: 600, color: "#64748B" }}>
                  / {questions.length} câu
                </Typography>
              </Box>

              <Button
                size="small"
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={() => setOpenPreview(true)}
                style={{
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: "0.82rem",
                  borderColor: "#2563EB",
                  color: "#2563EB",
                  textTransform: "none",
                }}
              >
                Xem thử như học viên
              </Button>
            </Box>

            <Box className={classes.footerBtnGroup}>
              <Button
                className={classes.btnSecondary}
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  setCreateQuestion(false);
                  if (listQuestionsAdded.length > 0) {
                    setIsPickOldExam(true);
                  } else {
                    setSearchParams({ step: "1" });
                  }
                }}
              >
                Quay lại
              </Button>

              <Button
                type="submit"
                className={classes.btnPrimary}
                endIcon={<ArrowForwardIcon />}
              >
                Tiếp theo
              </Button>
            </Box>
          </Box>
        </form>
      )}

      <ExamStudentPreviewModal
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        questions={questions}
        displayCount={valueInputShuffle}
        examTitle={valueForm?.title}
        isEssay={valueForm?.category === "essay"}
      />
    </div>
  );
}
